import { useEffect, useRef, useState } from "react";
import { ExternalLink, LayoutGrid } from "lucide-react";
import Reveal from "./Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { imageUrl, tiktokHandle } from "../../lib/api";

export function PublisherCard({ pub, testid }) {
  const handle = tiktokHandle(pub.tiktok_username);
  return (
    <div
      data-testid={testid}
      className="card-lift group relative overflow-hidden rounded-2xl border border-[#16233f] bg-[#0A0E17] aspect-[4/5]"
    >
      {pub.profile_image ? (
        <img
          src={imageUrl(pub.profile_image)}
          alt={pub.name}
          className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.04]"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1626] to-[#0A0E17] flex items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#0047FF] to-[#00F0FF] flex items-center justify-center">
            <span className="font-display font-700 text-3xl text-white">{pub.name?.charAt(0)?.toUpperCase()}</span>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-transparent pointer-events-none" />
      {pub.category && (
        <span className="absolute top-4 left-4 text-[11px] font-semibold bg-black/60 backdrop-blur border border-cyan-400/40 text-cyan-200 rounded-full px-3 py-1">
          {pub.category}
        </span>
      )}
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
        <h3 className="font-display font-700 text-xl text-white">{pub.name}</h3>
        {handle && <p className="text-sm text-cyan-300 mt-1">@{handle}</p>}
        {pub.description && (
          <p className="text-xs text-[#A0ABC0] mt-2.5 leading-relaxed line-clamp-2">{pub.description}</p>
        )}
        {pub.tiktok_url && (
          <a
            href={pub.tiktok_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testid}-tiktok-link`}
            className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-white bg-black/50 backdrop-blur border border-white/20 hover:border-cyan-400/60 hover:text-cyan-200 hover:shadow-[0_0_16px_rgba(0,240,255,0.25)] transition-all rounded-full px-4 py-2.5"
          >
            <ExternalLink size={13} strokeWidth={1.5} />
            TikTok Profiline Git
          </a>
        )}
      </div>
    </div>
  );
}

export default function Publishers({ publishers }) {
  const goToApply = () => document.getElementById("basvuru")?.scrollIntoView({ behavior: "smooth" });
  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || publishers.length < 5) return;
    let pos = el.scrollLeft;
    const timer = setInterval(() => {
      if (pausedRef.current) {
        pos = el.scrollLeft;
        return;
      }
      pos += 0.5;
      const half = el.scrollWidth / 2;
      if (half > 0 && pos >= half) pos -= half;
      el.scrollLeft = pos;
    }, 16);
    return () => clearInterval(timer);
  }, [publishers.length]);

  const loopPublishers = publishers.length >= 5 ? [...publishers, ...publishers] : publishers;

  return (
    <section id="yayincilar" data-testid="publishers-section" className="relative py-28 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <div className="flex items-end justify-between gap-4 mb-14">
            <h2 className="font-display font-700 text-3xl md:text-4xl text-white tracking-tight">
              Yayıncılarımız
            </h2>
            {publishers.length > 0 && (
              <button
                onClick={() => setShowAll(true)}
                data-testid="publishers-view-all-button"
                className="inline-flex items-center gap-2 text-xs font-semibold text-white border border-white/15 bg-black/40 hover:border-cyan-400/50 hover:text-cyan-200 transition-colors rounded-full px-4 py-2.5 shrink-0"
              >
                <LayoutGrid size={14} strokeWidth={1.5} />
                Tüm Yayıncıları Gör
              </button>
            )}
          </div>
        </Reveal>

        {publishers.length === 0 ? (
          <Reveal>
            <div data-testid="publishers-empty" className="rounded-2xl border border-dashed border-white/10 bg-[#0A0E17]/60 py-16 text-center">
              <p className="text-[#A0ABC0] text-sm">Yayıncılarımız çok yakında burada olacak.</p>
            </div>
          </Reveal>
        ) : (
          <div
            ref={scrollRef}
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
            data-testid="publishers-grid"
            className="flex gap-5 overflow-x-auto pb-4 hide-scrollbar"
          >
            {loopPublishers.map((p, i) => (
              <Reveal key={`${p.id}-${i}`} delay={(i % 4) * 80} className="shrink-0 w-[85%] sm:w-[46%] lg:w-[23.5%]">
                <PublisherCard pub={p} testid={i < publishers.length ? `publisher-card-${i}` : `publisher-card-dup-${i - publishers.length}`} />
              </Reveal>
            ))}
          </div>
        )}

        <Reveal delay={150}>
          <div className="mt-16 text-center">
            <p className="text-lg text-[#A0ABC0]">Sen de yayıncılarımız arasında yerini al.</p>
            <button
              onClick={goToApply}
              data-testid="publishers-apply-button"
              className="btn-glow mt-6 text-sm font-semibold text-white px-9 py-4 rounded-full"
            >
              Hemen Başvur
            </button>
          </div>
        </Reveal>
      </div>

      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="bg-[#050505] border-white/10 max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="publishers-all-dialog" aria-describedby="publishers-all-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-lg text-white">Tüm Yayıncılarımız</DialogTitle>
            <p id="publishers-all-desc" className="text-xs text-[#64748B]">Aktif tüm yayıncılarımızın listesi.</p>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="publishers-all-grid">
            {publishers.map((p, i) => (
              <PublisherCard key={p.id} pub={p} testid={`publisher-all-card-${i}`} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
